<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div class="d-flex align-items-center gap-2">
            <h3 class="detail-page-title mb-0">{{ lead.name }}</h3>
            <span v-if="lead.reference" class="text-muted small">({{ lead.reference }})</span>
            <span class="badge" :style="{ backgroundColor: getStatusColor(lead.status), color: '#fff' }">
              {{ getStatusName(lead.status) }}
            </span>
          </div>
          <div class="detail-page-actions">
            <button @click="openReminder" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-outline-primary btn-xs">
              <AppIcon name="bi-bell" class="bi bi-bell" style="font-size:12px" /> {{$t('reminder')}}
            </button>
            <button v-if="helper.checkAccess('edit_leads') != -1" @click="editLead" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" /> {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_leads') != -1" @click="deleteLead" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" /> {{$t('delete')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Content -->
      <div v-else-if="lead && lead.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">
              {{$t('attachments')}}
              <span v-if="lead.attachments && lead.attachments.length" class="badge bg-secondary ms-1">{{lead.attachments.length}}</span>
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">
              {{$t('history')}}
            </a>
          </li>
        </ul>

        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <div class="col-md-7">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0 small"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('lead')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{lead.reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{lead.name}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{lead.email}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{lead.phone}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('company')}}</small>
                      <strong>{{lead.company}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('city')}}</small>
                      <strong>{{lead.city}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('address')}}</small>
                      <strong>{{lead.address}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('source')}}</small>
                      <span class="badge bg-secondary">{{ lead.source || $t('no_source') }}</span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('value')}}</small>
                      <strong>{{ formatMoney(lead.value || 0) }}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('status')}}</small>
                      <span class="badge" :style="{ backgroundColor: getStatusColor(lead.status), color: '#fff' }">
                        {{ getStatusName(lead.status) }}
                      </span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{formatDate(lead.created_at)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('updated_at')}}</small>
                      <strong>{{formatDateTime(lead.updated_at)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('assigned_to')}}</small>
                      <strong>{{lead.user ? formatUserLabel(lead.user) : $t('unassigned')}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="reminders.length" class="card mb-4">
                <div class="card-header bg-white d-flex align-items-center justify-content-between">
                  <h6 class="mb-0"><AppIcon name="bi-bell" class="bi bi-bell me-2" />{{$t('scheduled_reminders')}}</h6>
                  <span class="badge rounded-pill bg-primary">{{reminders.length}}</span>
                </div>
                <div class="card-body">
                  <div v-for="reminder in reminders" :key="reminder.id" class="d-flex gap-3 pb-3 mb-3 border-bottom reminder-detail-item">
                    <AppIcon name="bi-clock" class="bi bi-clock text-primary mt-1" />
                    <div class="flex-grow-1">
                      <small class="text-muted d-block">{{$t('reminder_date')}}</small>
                      <strong class="d-block mb-1">{{formatDateTime(reminder.remind_at)}}</strong>
                      <small class="text-muted d-block">{{$t('reminder_message')}}</small>
                      <div style="white-space: pre-wrap; overflow-wrap: anywhere;">{{reminder.note}}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="card mb-4" v-if="lead.notes">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0" style="white-space: pre-wrap;">{{lead.notes}}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Comments -->
            <div class="col-md-5">
              <div class="card lead-comments-panel mb-4">
                <div class="card-header bg-white d-flex align-items-center justify-content-between">
                  <h6 class="mb-0"><AppIcon name="bi-chat-dots" class="bi bi-chat-dots me-2" />{{$t('comments')}}</h6>
                  <span v-if="lead.comments && lead.comments.length" class="badge rounded-pill bg-secondary">
                    {{lead.comments.length}}
                  </span>
                </div>

                <div class="card-body">
                  <!-- Add Comment -->
                  <div v-if="helper.checkAccess('edit_leads') != -1" class="mb-4">
                    <div class="d-flex gap-2">
                      <textarea v-model="newComment" class="form-control" rows="2" :placeholder="$t('write_a_comment')"></textarea>
                      <button class="btn btn-success align-self-end" :disabled="!newComment.trim() || savingComment" @click="addComment">
                        <span v-if="savingComment" class="spinner-border spinner-border-sm"></span>
                        <AppIcon name="bi-send" v-else class="bi bi-send" />
                      </button>
                    </div>
                  </div>

                  <!-- Comments List -->
                  <div v-if="lead.comments && lead.comments.length">
                    <div v-for="comment in sortedComments" :key="comment.id" class="d-flex gap-3 mb-3 pb-3 border-bottom">
                      <div class="flex-shrink-0">
                        <div class="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style="width: 36px; height: 36px; font-size: 14px;">
                          {{ comment.user ? comment.user.name.charAt(0).toUpperCase() : '?' }}
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <div class="d-flex justify-content-between align-items-start">
                          <div>
                            <strong class="small">{{ comment.user ? comment.user.name : '' }}</strong>
                            <span class="text-muted small ms-2">{{ formatDate(comment.created_at) }}</span>
                          </div>
                          <div v-if="helper.checkAccess('edit_leads') != -1" class="d-flex align-items-center gap-2">
                            <button
                              v-if="editingCommentId !== comment.id"
                              class="btn btn-link btn-sm text-primary p-0"
                              @click="startEditingComment(comment)"
                              :title="$t('edit')"
                            >
                              <AppIcon name="bi-pencil" class="bi bi-pencil" />
                            </button>
                            <button class="btn btn-link btn-sm text-danger p-0" @click="deleteComment(comment.id)" :title="$t('delete')">
                              <AppIcon name="bi-trash" class="bi bi-trash" />
                            </button>
                          </div>
                        </div>
                        <div v-if="editingCommentId === comment.id" class="mt-2">
                          <textarea v-model="editingCommentContent" class="form-control form-control-sm" rows="2"></textarea>
                          <div class="d-flex justify-content-end gap-2 mt-2">
                            <button class="btn btn-sm btn-light" :disabled="savingEditedComment" @click="cancelEditingComment">
                              {{$t('cancel')}}
                            </button>
                            <button
                              class="btn btn-sm btn-success"
                              :disabled="!editingCommentContent.trim() || savingEditedComment"
                              @click="updateComment(comment)"
                            >
                              <span v-if="savingEditedComment" class="spinner-border spinner-border-sm me-1"></span>
                              {{$t('update')}}
                            </button>
                          </div>
                        </div>
                        <p v-else class="mb-0 mt-1" style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ comment.content }}</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center text-muted py-4">
                    <AppIcon name="bi-chat-dots" class="bi bi-chat-dots" style="font-size: 2rem;" />
                    <p class="mt-2 mb-0">{{ $t('no_comments_yet') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Attachments Tab -->
        <div v-if="step === 'attachments'">
          <div class="card mb-4">
            <div class="card-header bg-white d-flex justify-content-between align-items-center">
              <h6 class="mb-0"><AppIcon name="bi-paperclip" class="bi bi-paperclip me-2" />{{$t('attachments')}}</h6>
              <label v-if="helper.checkAccess('edit_leads') != -1" class="btn btn-sm btn-outline-primary mb-0">
                <AppIcon name="bi-upload" class="bi bi-upload me-1" />{{$t('upload')}}
                <input type="file" @change="uploadAttachment" class="d-none" multiple>
              </label>
            </div>
            <div class="card-body">
              <div v-if="lead.attachments && lead.attachments.length">
                <div v-for="attachment in lead.attachments" :key="attachment.id" class="d-flex align-items-center gap-3 mb-2 pb-2 border-bottom">
                  <AppIcon name="bi-file-earmark" class="bi bi-file-earmark" style="font-size: 1.5rem;" />
                  <div class="flex-grow-1">
                    <strong class="small d-block">{{ attachment.filename }}</strong>
                    <small class="text-muted">{{ formatFileSize(attachment.filesize) }}</small>
                  </div>
                  <a v-if="attachment.filepath" :href="attachment.filepath" target="_blank" class="btn btn-sm btn-outline-primary">
                    <AppIcon name="bi-download" class="bi bi-download" />
                  </a>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                <AppIcon name="bi-paperclip" class="bi bi-paperclip" style="font-size: 2rem;" />
                <p class="mt-2">{{ $t('no_attachments_yet') }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="step === 'history'">
          <document-history :history="history" />
        </div>

      </div>
    </div>

    <!-- Edit Modal -->
    <div class="modal fade" id="edit-lead-modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title mb-0">
              <AppIcon name="bi-pencil-square" class="bi bi-pencil-square me-2" />{{$t('edit_lead')}}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body pt-2" style="text-align:left">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">{{$t('name')}} <span class="text-danger">*</span></label>
                <input v-model="editData.name" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('email')}}</label>
                <input v-model="editData.email" type="email" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('phone')}}</label>
                <input v-model="editData.phone" type="tel" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('company')}}</label>
                <input v-model="editData.company" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('city')}}</label>
                <input v-model="editData.city" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('address')}}</label>
                <input v-model="editData.address" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('source')}}</label>
                <select v-if="configuredLeadSources.length" v-model="editData.source" class="form-select">
                  <option value="">{{$t('select_source')}}</option>
                  <option v-for="source in sourceOptions" :key="source" :value="source">{{source}}</option>
                </select>
                <input v-else v-model="editData.source" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('status')}}</label>
                <select v-model="editData.status" class="form-select">
                  <option v-for="s in statuses" :key="s.id" :value="s.name">{{s.name}}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('value')}}</label>
                <input v-model="editData.value" type="number" step="0.01" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('assigned_to')}}</label>
                <select v-model="editData.user_id" class="form-select">
                  <option value="">{{$t('unassigned')}}</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">{{formatUserLabel(user)}}</option>
                </select>
              </div>
              <div class="col-12">
                <collapsible-notes-field v-model="editData.notes" id="lead-details-notes" rows="3" />
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{$t('close')}}</button>
            <button type="button" class="btn btn-success" :disabled="saving || !editData.name" @click="saveEdit">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg me-1" />{{$t('update')}}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ReminderModal
      v-if="lead && lead.id"
      ref="reminderModal"
      entity-type="lead"
      :entity-id="lead.id"
    />
  </div>
</template>

<script>
import helper from '../../helper'
import ReminderModal from '../../components/ReminderModal.vue'
import DocumentHistory from '../../components/DocumentHistory.vue'

export default {
  name: 'LeadDetails',
  components: { ReminderModal, DocumentHistory },
  data() {
    return {
      lead: {},
      editData: {},
      statuses: [],
      users: [],
      history: [],
      reminders: [],
      loading: false,
      saving: false,
      savingComment: false,
      savingEditedComment: false,
      step: 'overview',
      newComment: '',
      editingCommentId: null,
      editingCommentContent: '',
      editModal: null,
    }
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS;
    },
    helper() {
      return helper;
    },
    configuredLeadSources() {
      const sources = this.business?.metadata?.lead_sources;
      if (!Array.isArray(sources)) return [];
      return [...new Set(sources.map(source => String(source || '').trim()).filter(Boolean))];
    },
    sourceOptions() {
      const sources = [
        ...this.configuredLeadSources,
        this.lead.source,
        this.editData.source
      ].map(source => String(source || '').trim()).filter(Boolean);
      return [...new Set(sources)];
    },
    sortedComments() {
      if (!this.lead.comments) return [];
      return [...this.lead.comments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  },
  mounted() {
    if (helper.checkAccess('view_leads') == -1) return this.$router.push({ path: '/' });
    if (helper.checkAccess('view_leads') == -2) return this.$router.push({ path: '/subscribe' });
    this.loadStatuses();
    this.loadAssignees();
    this.getLead();
    this.getHistory();
    this.loadReminders();
    window.addEventListener('reminders:changed', this.loadReminders);
    // eslint-disable-next-line no-undef
    this.editModal = new bootstrap.Modal(document.getElementById('edit-lead-modal'), { backdrop: 'static' });
  },
  beforeDestroy() {
    window.removeEventListener('reminders:changed', this.loadReminders);
  },
  methods: {
    formatMoney(n) { return helper.formatMoney(n); },
    formatDate(d) { return helper.formatDate(d); },
    formatDateTime(d) { return helper.formatDateTime(d); },
    formatUserLabel(user) {
      if (!user) return this.$t('unassigned');
      return user.name || user.email || '';
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
    },
    formatHistoryAction(item) {
      if (item.action === 'UPDATE') return this.$t('lead_updated');
      if (item.action === 'CREATED') return this.$t('lead_created');
      if (item.action === 'COMMENT_ADDED') return this.$t('comment_added');
      if (item.action === 'COMMENT_UPDATED') return this.$t('comment_updated');
      if (item.action === 'COMMENT_DELETED') return this.$t('comment_deleted');
      return item.action;
    },
    openReminder() {
      this.$refs.reminderModal.open();
    },
    async loadReminders() {
      try {
        const query = new URLSearchParams({
          entity_type: 'lead',
          entity_id: this.$route.params.id,
          status: 'scheduled',
          limit: '50'
        });
        const response = await helper.customGet(`${process.env.VUE_APP_API}/reminders?${query.toString()}`);
        this.reminders = response.data.data || [];
      } catch (e) {
        this.reminders = [];
      }
    },

    // Statuses
    async loadStatuses() {
      try {
        const res = await helper.customGet(`${process.env.VUE_APP_API}/lead-statuses`);
        this.statuses = res.data;
      } catch (e) { /* ignore */ }
    },
    async loadAssignees() {
      try {
        const res = await helper.customGet(`${process.env.VUE_APP_API}/leads/assignees`);
        this.users = res.data || [];
      } catch (e) { /* ignore */ }
    },
    getStatusName(status) {
      const s = this.statuses.find(s => s.name === status);
      return s ? s.name : status || '';
    },
    getStatusColor(status) {
      const s = this.statuses.find(s => s.name === status);
      return s ? s.color : '#6c757d';
    },

    // Lead
    async getLead() {
      try {
        this.loading = true;
        const res = await helper.customGet(`${process.env.VUE_APP_API}/leads/${this.$route.params.id}`);
        this.lead = res.data;
        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    async getHistory() {
      try {
        const res = await helper.customGet(`${process.env.VUE_APP_API}/leads/${this.$route.params.id}/history`);
        this.history = res.data;
      } catch (e) { /* ignore */ }
    },

    editLead() {
      this.editData = { ...this.lead, user_id: this.lead.user_id || '' };
      this.editModal.show();
    },
    async saveEdit() {
      try {
        this.saving = true;
        const payload = JSON.parse(JSON.stringify(this.editData));
        delete payload.id;
        delete payload.reference;
        delete payload.user;
        delete payload.created_at;
        delete payload.updated_at;
        delete payload.deleted_at;
        delete payload.history;
        delete payload.comments;
        delete payload.attachments;

        await helper.customPatch(`${process.env.VUE_APP_API}/leads/${this.lead.id}`, payload);
        this.saving = false;
        this.editModal.hide();
        this.getLead();
        this.getHistory(); // Refresh history
      } catch (e) {
        this.saving = false;
      }
    },
    async deleteLead() {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      });
      if (result.isConfirmed) {
        await helper.customDelete(`${process.env.VUE_APP_API}/leads/${this.lead.id}`);
        this.$router.push('/leads');
      }
    },

    // Comments
    async addComment() {
      try {
        this.savingComment = true;
        const res = await helper.customPost(`${process.env.VUE_APP_API}/leads/${this.lead.id}/comments`, { content: this.newComment });
        const { lead_updated_at: leadUpdatedAt, ...comment } = res.data;
        if (!this.lead.comments) this.lead.comments = [];
        this.lead.comments.push(comment);
        if (leadUpdatedAt) this.$set(this.lead, 'updated_at', leadUpdatedAt);
        this.newComment = '';
        this.getHistory();
        this.savingComment = false;
      } catch (e) {
        this.savingComment = false;
      }
    },
    startEditingComment(comment) {
      this.editingCommentId = comment.id;
      this.editingCommentContent = comment.content;
    },
    cancelEditingComment() {
      this.editingCommentId = null;
      this.editingCommentContent = '';
    },
    async updateComment(comment) {
      const content = this.editingCommentContent.trim();
      if (!content || this.savingEditedComment) return;
      if (content === comment.content) {
        this.cancelEditingComment();
        return;
      }

      try {
        this.savingEditedComment = true;
        const res = await helper.customPatch(`${process.env.VUE_APP_API}/leads/comments/${comment.id}`, { content });
        const { lead_updated_at: leadUpdatedAt, ...updatedComment } = res.data;
        const commentIndex = this.lead.comments.findIndex(item => item.id === comment.id);
        if (commentIndex !== -1) this.$set(this.lead.comments, commentIndex, updatedComment);
        if (leadUpdatedAt) this.$set(this.lead, 'updated_at', leadUpdatedAt);
        this.cancelEditingComment();
        this.getHistory();
      } catch (e) {
        // Request errors are displayed by the shared API helper.
      } finally {
        this.savingEditedComment = false;
      }
    },
    async deleteComment(commentId) {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete_it')
      });
      if (result.isConfirmed) {
        const res = await helper.customDelete(`${process.env.VUE_APP_API}/leads/comments/${commentId}`);
        this.lead.comments = this.lead.comments.filter(c => c.id !== commentId);
        if (res.data?.lead_updated_at) this.$set(this.lead, 'updated_at', res.data.lead_updated_at);
        if (this.editingCommentId === commentId) this.cancelEditingComment();
        this.getHistory();
      }
    },

    // Attachments
    async uploadAttachment(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i++) {
        try {
          await helper.customUploadFile(files[i], `businesses/${this.business.id}/leads/${this.lead.id}/${helper.generateRandString(20)}`, {
            entity_type: 'lead',
            entity_id: this.lead.id
          });
        } catch (e) { /* ignore */ }
      }
      this.getLead(); // Refresh to get new attachments
    }
  }
}
</script>
